// src/components/home-new/TheBeamline.jsx
import React, { useRef, useEffect, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const steps = [
  {
    id: '01',
    title: 'Scattering',
    desc: 'Charged particles passing through matter receive many small deflections. Lower momentum scatters more. Thicker materials scatter more. The beam fans out - and then you understand why the Highland formula exists.',
    accent: '#22d3ee',
    icon: '◎',
  },
  {
    id: '02',
    title: 'Energy Loss',
    desc: 'Protons deposit energy differently from photons - their dose peaks near the end of range. Change beam energy and watch the Bragg peak shift. This is the basis of proton therapy and it connects particle physics to patient care.',
    accent: '#f472b6',
    icon: '↯',
  },
  {
    id: '03',
    title: 'Ring Imaging',
    desc: 'A detector can transform particle speed into geometry. Faster particles produce larger Cherenkov rings. Suddenly, separating pions from kaons from protons is not abstract - it is something you can see.',
    accent: '#a3e635',
    icon: '✦',
  },
  {
    id: '04',
    title: 'Bending',
    desc: 'We do not read momentum from a label on the particle. We infer it from the curve of its trajectory in a magnetic field. That is indirect measurement - and it is one of the deepest ideas in experimental physics.',
    accent: '#facc15',
    icon: '⊛',
  },
  {
    id: '05',
    title: 'Discrimination',
    desc: 'Detectors can identify particles not only by where they go, but by what extra radiation they produce. Comparing electron and pion response reveals that detector design is an engineering answer to a physics question.',
    accent: '#818cf8',
    icon: '◈',
  },
  {
    id: '06',
    title: 'Imaging',
    desc: 'Cosmic muons penetrate deeply. Dense hidden objects alter their scattering signatures. Images can be reconstructed from many indirect traces - demonstrating that particle physics methods reach far beyond the laboratory.',
    accent: '#22d3ee',
    icon: '∞',
  },
];

const TheBeamline = () => {
  const sectionRef = useRef(null);
  const introRef = useRef(null);
  const stackRef = useRef(null);
  const cardsRef = useRef([]);
  const progressRef = useRef(null);
  const counterRef = useRef(null);
  const outroRef = useRef(null);
  const bgImageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const totalSteps = steps.length;
      const scrollPerStep = 600; // scroll pixels per card transition
      const totalScroll = scrollPerStep * (totalSteps + 1.5); // +1.5 for intro & outro

      // Main pinned timeline
      const masterTL = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: () => `+=${totalScroll}`,
          scrub: 0.6,
          pin: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        }
      });

      // Phase 1: Intro text fades out + first card enters
      masterTL.to(introRef.current, {
        opacity: 0,
        scale: 0.9,
        y: -60,
        filter: 'blur(8px)',
        duration: 0.8,
        ease: 'power2.in',
      });

      // Phase 2: Cards sequence - each card enters, holds, then exits
      cardsRef.current.forEach((card, i) => {
        if (!card) return;

        const cardGlow = card.querySelector('.card-glow');

        // Card enters: scale up from below
        masterTL.fromTo(card, 
          { 
            yPercent: 60,
            scale: 0.75,
            opacity: 0,
          },
          { 
            yPercent: 0,
            scale: 1,
            opacity: 1,
            duration: 1,
            ease: 'power3.out',
          }
        );

        // Glow blooms
        if (cardGlow) {
          masterTL.fromTo(cardGlow,
            { opacity: 0, scale: 0.3 },
            { opacity: 1, scale: 1, duration: 0.5 },
            '<0.3'
          );
        }

        // Number counter ticks
        if (counterRef.current) {
          masterTL.to(counterRef.current, {
            innerText: i + 1,
            snap: { innerText: 1 },
            duration: 0.01,
          }, '<');
        }

        // Progress bar (vertical - scaleY)
        if (progressRef.current) {
          masterTL.to(progressRef.current, {
            scaleY: (i + 1) / totalSteps,
            duration: 0.5,
          }, '<');
        }

        // Hold (breathing room)
        masterTL.to(card, { duration: 0.6 });

        // ALL cards exit - including last one
        masterTL.to(card, {
          yPercent: -50,
          scale: 0.7,
          opacity: 0,
          duration: 0.8,
          ease: 'power2.in',
        });
        if (cardGlow) {
          masterTL.to(cardGlow, { opacity: 0, duration: 0.3 }, '<');
        }
      });

      // Phase 3: Outro appears cleanly (no card behind it)
      masterTL.fromTo(outroRef.current,
        { opacity: 0, y: 60, scale: 0.95 },
        { opacity: 1, y: 0, scale: 1, duration: 1 },
        '<0.3'
      );

      // Background parallax effect: slowly scale up and shift during the entire scroll
      if (bgImageRef.current) {
        masterTL.to(bgImageRef.current, {
          scale: 1.1,
          yPercent: -2, // Very slight upward drift so we don't cut it off
          ease: 'none',
          duration: masterTL.duration(), // Spans the whole timeline
        }, 0); // Start at the very beginning of the timeline
      }

    });

    return () => ctx.revert();
  }, []);

  return (
    <section 
      ref={sectionRef} 
      data-cursor-invert
      className="relative bg-transparent overflow-hidden"
    >
      {/* Collage Background with 3D Depth & Blur */}
      <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden" style={{ perspective: '1200px' }}>
        {/* The collage image - blurred, 3D-transformed, subtly animated */}
        <div
          ref={bgImageRef}
          className="absolute inset-0 flex items-center justify-center p-4 md:p-8"
          style={{
            transform: 'rotateX(4deg) rotateY(-2deg) scale(1.05)',
            transformOrigin: 'center center',
            animation: 'collage-drift 20s ease-in-out infinite',
            willChange: 'transform',
          }}
        >
          <img
            src="/images/collage-students.jpg"
            alt="Students using the physics simulator"
            aria-hidden="true"
            className="w-full h-full object-contain opacity-80"
            style={{
              filter: 'blur(4px) saturate(0.6) brightness(0.6)',
            }}
          />
        </div>
        {/* Dark gradient overlay for readability */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 40%, rgba(0,0,0,0.6) 70%, rgba(0,0,0,0.85) 100%)',
          }}
        />
        {/* Vignette for 3D depth illusion */}
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.7) 100%)',
          }}
        />

        {/* Ambient glows */}
        <div className="absolute top-1/4 left-1/3 w-[60vw] h-[60vw] bg-slate-300/[0.02] rounded-full blur-[180px] mix-blend-screen" />
        <div className="absolute bottom-1/3 right-1/4 w-[50vw] h-[50vw] bg-cyan-200/[0.015] rounded-full blur-[160px] mix-blend-screen" />
        {/* Grid dots */}
        <div className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1.5px 1.5px, rgba(255,255,255,0.35) 1px, transparent 0)',
            backgroundSize: '60px 60px',
          }}
        />
        {/* Floating particles */}
        {[...Array(6)].map((_, i) => (
          <div
            key={`p-${i}`}
            className="absolute h-1 w-1 rounded-full bg-cyan-200/20 blur-[1px] animate-pulse"
            style={{
              top: `${15 + Math.random() * 70}%`,
              left: `${10 + Math.random() * 80}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${4 + Math.random() * 3}s`,
            }}
          />
        ))}
      </div>

      {/* Keyframe for subtle collage drift animation */}
      <style>{`
        @keyframes collage-drift {
          0%, 100% {
            transform: rotateX(8deg) rotateY(-3deg) scale(1.05) translateZ(0);
          }
          25% {
            transform: rotateX(6deg) rotateY(-1deg) scale(1.08) translateZ(10px);
          }
          50% {
            transform: rotateX(10deg) rotateY(-5deg) scale(1.02) translateZ(-5px);
          }
          75% {
            transform: rotateX(7deg) rotateY(-2deg) scale(1.06) translateZ(8px);
          }
        }
      `}</style>

      {/* === Pinned viewport === */}
      <div className="relative h-screen w-full flex items-center justify-center">

        {/* Left side: title + progress (desktop) */}
        <div className="absolute left-6 top-1/2 -translate-y-1/2 z-30 hidden md:flex flex-col items-start gap-6 md:left-10 lg:left-16">
          {/* Vertical progress track */}
          <div className="relative h-48 w-px bg-white/10 rounded-full overflow-hidden">
            <div
              ref={progressRef}
              className="absolute top-0 left-0 w-full bg-gradient-to-b from-cyan-400 to-cyan-400/40 rounded-full origin-top"
              style={{ height: '100%', transform: 'scaleY(0)' }}
            />
          </div>
          {/* Step counter */}
          <div className="flex items-baseline gap-1 font-mono text-xs text-white/30">
            <span ref={counterRef} className="text-cyan-400 text-lg font-bold">0</span>
            <span>/ {steps.length}</span>
          </div>
        </div>

        {/* Intro text - fades out as cards begin */}
        <div ref={introRef} className="absolute inset-0 flex items-center z-20 px-6 sm:px-10 md:px-24">
          <div className="max-w-3xl">
            <span className="home-kicker mb-5 block">The Physics</span>
            <h2 className="text-[13vw] font-bold leading-none tracking-tight text-white sm:text-[9vw] md:text-[6vw]">
              Six principles you <br />
              <span className="text-cyan-400 glow">can explore.</span>
            </h2>
            <p className="mt-6 max-w-xl text-sm font-light leading-relaxed text-white/45 sm:mt-8 sm:max-w-2xl md:text-lg">
              Each principle is drawn from real experimental physics - scattering, energy loss, particle identification, momentum reconstruction, signal discrimination, and indirect imaging. Run the simulation and see the idea come alive.
            </p>
          </div>
        </div>

        {/* Card stack area - cards fly in/out here */}
          <div ref={stackRef} className="relative z-10 w-full max-w-2xl mx-auto px-6">
          {steps.map((step, i) => (
            <div
              key={step.id}
              ref={el => cardsRef.current[i] = el}
              className="absolute inset-0 flex items-center justify-center"
              style={{ 
                opacity: 0,
                willChange: 'transform, opacity',
              }}
            >
              {/* Glow */}
              <div
                className="card-glow absolute w-[80%] h-[80%] rounded-full blur-[100px] pointer-events-none opacity-0"
                style={{ background: `radial-gradient(circle, ${step.accent}18, transparent 70%)` }}
              />
              
              {/* Card */}
              <div className="card-inner relative w-full rounded-2xl border border-white/[0.08] bg-black/60 backdrop-blur-md p-8 md:p-12 transition-[border-color,box-shadow] duration-500"
                style={{
                  boxShadow: `0 30px 80px -20px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06)`,
                }}
              >
                {/* Top accent strip */}
                <div className="absolute top-0 left-0 right-0 h-[2px] rounded-t-2xl overflow-hidden">
                  <div className="h-full w-full" style={{ background: `linear-gradient(90deg, transparent 5%, ${step.accent}, transparent 95%)` }} />
                </div>

                {/* Icon + number row */}
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl border"
                      style={{ 
                        borderColor: `${step.accent}30`,
                        background: `${step.accent}10`,
                        color: step.accent,
                      }}
                    >
                      {step.icon}
                    </div>
                    <div className="flex flex-col">
                      <span className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: step.accent }}> Step {step.id}</span>
                    </div>
                  </div>
                  <div className="text-6xl font-black text-white/[0.04] font-mono select-none">{step.id}</div>
                </div>

                <h3 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">{step.title}</h3>
                <p className="text-gray-400 leading-relaxed font-light text-base md:text-lg max-w-lg">
                  {step.desc}
                </p>

                {/* Bottom accent line */}
                <div className="absolute bottom-0 left-[10%] right-[10%] h-px" style={{ background: `linear-gradient(90deg, transparent, ${step.accent}25, transparent)` }} />
              </div>
            </div>
          ))}
        </div>

        {/* Outro - "The Handoff" appears after last card */}
        <div ref={outroRef} className="absolute inset-0 flex items-center justify-center z-20 px-6 opacity-0">
          <div className="home-panel relative z-10 w-full max-w-3xl px-6 py-7 sm:px-8 sm:py-8 md:px-10 md:py-10 text-center">
            <span className="home-kicker mb-4 block">The Handoff</span>
            <h2 className="mb-4 text-3xl font-bold tracking-tight text-white sm:text-4xl md:text-5xl">
              Now see the
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-indigo-400"> engine underneath.</span>
            </h2>
            <p className="text-sm font-light leading-relaxed text-gray-400 sm:text-base md:text-lg max-w-2xl mx-auto">
              Every track, ring, dose curve, and distribution is calculated from a physical model. The next section explains what is being computed and why simplification is actually the point.
            </p>
          </div>
        </div>

        {/* Mobile progress dots */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 md:hidden z-30">
          {steps.map((step, i) => (
            <div key={i} className="w-1.5 h-1.5 rounded-full bg-white/20" />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TheBeamline;